import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

// Global flag to prevent multiple token exchanges
let isTokenExchangeInProgress = false;

export interface CognitoUser {
  sub: string;
  email: string;
  name: string;
  'custom:role': string;
  'custom:profile_completed': string;
  profile_image_url?: string;
  exp?: number;
  iat?: number;
  aud?: string;
  iss?: string;
}

interface AuthContextType {
  user: CognitoUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  userRole: 'admin' | 'user' | null;
  redirectToLogin: () => void;
  redirectToSignup: () => void;
  logout: () => void;
  forceLogout: () => void;
  handleAuthCallback: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);

  // Get Cognito configuration from environment
  const getCognitoConfig = () => {
    // Use the Cognito Hosted UI URL from environment if available
    const hostedUiUrl = process.env.REACT_APP_COGNITO_HOSTED_UI_URL;
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || 'placeholder-client-id';
    let cognitoDomain: string;

    console.log('🔧 Cognito config environment variables:', {
      hostedUiUrl,
      clientId,
      origin: window.location.origin
    });

    if (hostedUiUrl && hostedUiUrl.trim() !== '') {
      // Extract domain from the hosted UI URL (e.g., "https://domain.auth.region.amazoncognito.com" -> "domain.auth.region.amazoncognito.com")
      cognitoDomain = hostedUiUrl.replace('https://', '');
    } else {
      // Fallback to constructing domain from API URL (for local development)
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.devtalks.headforthe.cloud';
      const apiDomain = baseUrl.replace('https://api.', '');
      cognitoDomain = `auth.${apiDomain}`;
    }

    const config = {
      domain: cognitoDomain,
      clientId,
      redirectUri: `${window.location.origin}/auth/callback`,
      logoutUri: `${window.location.origin}/auth/logout`,
      responseType: 'code',
      scope: 'openid email profile'
    };

    console.log('🔧 Final Cognito config:', config);

    return config;
  };

  const redirectToLogin = () => {
    const config = getCognitoConfig();
    const params = new URLSearchParams({
      client_id: config.clientId,
      response_type: config.responseType,
      scope: config.scope,
      redirect_uri: config.redirectUri
    });

    window.location.href = `https://${config.domain}/login?${params.toString()}`;
  };

  const redirectToSignup = () => {
    const config = getCognitoConfig();
    const params = new URLSearchParams({
      client_id: config.clientId,
      response_type: config.responseType,
      scope: config.scope,
      redirect_uri: config.redirectUri
    });

    window.location.href = `https://${config.domain}/signup?${params.toString()}`;
  };

  const logout = () => {
    const config = getCognitoConfig();

    // Clear local storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('id_token');

    // Clear user state
    setUser(null);

    // Redirect to Cognito logout
    const params = new URLSearchParams({
      client_id: config.clientId,
      logout_uri: config.logoutUri
    });

    window.location.href = `https://${config.domain}/logout?${params.toString()}`;
  };

  const forceLogout = () => {
    console.log('🔄 Force logout initiated');

    // Clear all possible authentication data
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('id_token');

    // Clear any other auth-related data that might exist
    localStorage.removeItem('user');
    localStorage.removeItem('auth_state');

    // Clear session storage as well
    sessionStorage.clear();

    // Clear user state immediately
    setUser(null);

    // Force redirect to home page without Cognito logout (in case Cognito is unreachable)
    window.location.href = '/';
  };

  const handleAuthCallback = async () => {
    // Prevent concurrent processing globally
    if (isTokenExchangeInProgress || isProcessingCallback) {
      console.log('🔄 Token exchange already in progress, skipping...');
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    if (error) {
      console.error('Authentication error:', error);
      throw new Error(`Authentication failed: ${error}`);
    }

    if (code) {
      // Check if we already have tokens to prevent double processing
      const existingToken = localStorage.getItem('access_token');
      if (existingToken) {
        console.log('🔄 Tokens already exist, skipping token exchange');
        return;
      }

      isTokenExchangeInProgress = true;
      setIsProcessingCallback(true);
      try {
        // Exchange code for tokens directly with Cognito
        const config = getCognitoConfig();
        const tokenEndpoint = `https://${config.domain}/oauth2/token`;

        const tokenParams = new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: config.clientId,
          code: code,
          redirect_uri: config.redirectUri
        });

        console.log('🔄 Token exchange request:', {
          endpoint: tokenEndpoint,
          clientId: config.clientId,
          redirectUri: config.redirectUri,
          code: code.substring(0, 10) + '...' // Log partial code for debugging
        });

        const tokenResponse = await fetch(tokenEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: tokenParams.toString()
        });

        console.log('🔄 Token response status:', tokenResponse.status);
        console.log('🔄 Token response headers:', Object.fromEntries(tokenResponse.headers.entries()));

        if (!tokenResponse.ok) {
          const errorData = await tokenResponse.text();
          console.error('❌ Token exchange failed:', {
            status: tokenResponse.status,
            statusText: tokenResponse.statusText,
            errorData: errorData,
            requestBody: tokenParams.toString(),
            endpoint: tokenEndpoint
          });
          throw new Error(`Token exchange failed: ${tokenResponse.status} ${tokenResponse.statusText} - ${errorData}`);
        }

        const tokenData = await tokenResponse.json();
        const { access_token, refresh_token, id_token } = tokenData;

        // Store tokens
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        localStorage.setItem('id_token', id_token);

        // Decode and set user from ID token
        const userInfo = parseJWT(id_token);
        setUser(userInfo);

        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('Token exchange failed:', error);
        throw error;
      } finally {
        isTokenExchangeInProgress = false;
        setIsProcessingCallback(false);
      }
    }
  };

  const refreshToken = useCallback(async () => {
    const refreshTokenValue = localStorage.getItem('refresh_token');

    if (!refreshTokenValue) {
      throw new Error('No refresh token available');
    }

    try {
      // Refresh tokens directly with Cognito
      const config = getCognitoConfig();
      const tokenEndpoint = `https://${config.domain}/oauth2/token`;

      const refreshParams = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: config.clientId,
        refresh_token: refreshTokenValue
      });

      const refreshResponse = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: refreshParams.toString()
      });

      if (!refreshResponse.ok) {
        const errorData = await refreshResponse.text();
        console.error('Token refresh failed:', errorData);
        throw new Error(`Token refresh failed: ${refreshResponse.status} ${refreshResponse.statusText}`);
      }

      const tokenData = await refreshResponse.json();
      const { access_token, id_token } = tokenData;

      // Update stored tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('id_token', id_token);

      // Update user info from JWT
      const userInfo = parseJWT(id_token);

      // Fetch profile to get profile_image_url
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.devtalks.headforthe.cloud';
        const profileResponse = await fetch(`${apiBaseUrl}/v1/profile`, {
          headers: {
            'Authorization': `Bearer ${id_token}`
          }
        });

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          const profile = profileData.data?.profile;
          if (profile?.profile_image_url) {
            userInfo.profile_image_url = profile.profile_image_url;
          }
        }
      } catch (profileError) {
        // Profile fetch failed, but token refresh succeeded
        console.warn('Failed to fetch profile image:', profileError);
      }

      setUser(userInfo);
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Clear tokens and redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('id_token');
      setUser(null);
      throw error;
    }
  }, []);

  const parseJWT = (token: string): CognitoUser => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to parse JWT:', error);
      throw new Error('Invalid token format');
    }
  };

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('id_token');

      if (token) {
        try {
          const userInfo = parseJWT(token);

          // Check if token is expired
          const currentTime = Date.now() / 1000;
          if (userInfo.exp && userInfo.exp < currentTime) {
            // Try to refresh token
            await refreshToken();
          } else {
            // Fetch profile to get profile_image_url
            try {
              const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.devtalks.headforthe.cloud';
              const profileResponse = await fetch(`${apiBaseUrl}/v1/profile`, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });

              if (profileResponse.ok) {
                const profileData = await profileResponse.json();
                const profile = profileData.data?.profile;
                if (profile?.profile_image_url) {
                  userInfo.profile_image_url = profile.profile_image_url;
                }
              }
            } catch (profileError) {
              // Profile fetch failed, but auth is still valid
              console.warn('Failed to fetch profile image:', profileError);
            }

            setUser(userInfo);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          // Clear invalid tokens
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('id_token');
        }
      }

      setIsLoading(false);
    };

    checkAuth();
  }, [refreshToken]);

  const isAuthenticated = !!user;
  const userRole = user?.['custom:role'] as 'admin' | 'user' | null;

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    userRole,
    redirectToLogin,
    redirectToSignup,
    logout,
    forceLogout,
    handleAuthCallback,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
